import {
	indexItems,
	recipGroupSignature,
	childrenSignature,
	computeRecipGroupChildren,
	computeBaseChildren,
	normalizeChildren,
} from "./OryxplaceOrderChildrenFactory"

const str = v => String(v ?? "").trim()
const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0)

const numText = v => {
	const n = num(v)
	return n.toFixed(6).replace(/\.?0+$/, "")
}

const itemSignature = item => {
	const ident = num(item?.vdpident)
	const insumo = str(item?.vdpinsumo)
	const recip = str(item?.vdprecip)
	const qty = numText(item?.vdpqtde)
	const cap = numText(item?.in9capac)
	return [ident, insumo, recip, qty, cap].join("|")
}

export function createVdpChildrenCacheManager() {
	let recipSigByRecip = new Map()
	let parentSigByParentKey = new Map()
	let childSigByParentKey = new Map()
	let childrenByParentKey = new Map()

	const clearAll = () => {
		recipSigByRecip = new Map()
		parentSigByParentKey = new Map()
		childSigByParentKey = new Map()
		childrenByParentKey = new Map()
	}

	const setChildrenForPk = (pk, nextChildren) => {
		const nextSig = childrenSignature(nextChildren)
		const prevSig = childSigByParentKey.get(pk)
		if (nextSig === prevSig && childrenByParentKey.has(pk)) return
		childSigByParentKey.set(pk, nextSig)
		childrenByParentKey.set(pk, nextChildren)
	}

	const recomputeAll = ({ soldMap, returnedMap }) => {
		const { itemsByParentKey, parentKeysByRecip } = indexItems(soldMap, returnedMap)

		recipSigByRecip = new Map()
		parentSigByParentKey = new Map()
		childSigByParentKey = new Map()
		childrenByParentKey = new Map()

		const handled = new Set()

		for (const [recip, rawKeys] of parentKeysByRecip.entries()) {
			const parentKeys = [...(rawKeys || [])].sort()
			recipSigByRecip.set(recip, recipGroupSignature(parentKeys, itemsByParentKey))

			const computed = computeRecipGroupChildren(parentKeys, itemsByParentKey)
			for (const [pk, children] of computed.entries()) {
				handled.add(pk)

				const parent = itemsByParentKey.get(pk)
				parentSigByParentKey.set(pk, itemSignature(parent))

				const normalized = normalizeChildren(children)
				setChildrenForPk(pk, normalized)
			}
		}

		for (const [pk, parent] of itemsByParentKey.entries()) {
			if (handled.has(pk)) continue

			parentSigByParentKey.set(pk, itemSignature(parent))

			const base = normalizeChildren(computeBaseChildren(parent))
			setChildrenForPk(pk, base)
		}
	}

	const updateFromMaps = ({ soldMap, returnedMap }) => {
		const { itemsByParentKey, parentKeysByRecip } = indexItems(soldMap, returnedMap)

		const currentParentKeys = new Set(itemsByParentKey.keys())

		for (const pk of Array.from(parentSigByParentKey.keys())) {
			if (currentParentKeys.has(pk)) continue
			parentSigByParentKey.delete(pk)
			childSigByParentKey.delete(pk)
			childrenByParentKey.delete(pk)
		}

		const dirtyRecipKeys = new Map()

		const allRecips = new Set([...recipSigByRecip.keys(), ...parentKeysByRecip.keys()])
		for (const recip of allRecips) {
			const rawKeys = parentKeysByRecip.get(recip) || []
			if (!rawKeys.length) {
				recipSigByRecip.delete(recip)
				continue
			}

			const parentKeys = [...rawKeys].sort()
			const nextSig = recipGroupSignature(parentKeys, itemsByParentKey)
			const prevSig = recipSigByRecip.get(recip)

			if (nextSig !== prevSig) {
				recipSigByRecip.set(recip, nextSig)
				dirtyRecipKeys.set(recip, parentKeys)
			}
		}

		const forcedRecips = new Set()
		const dirtyParentKeys = []

		for (const [pk, parent] of itemsByParentKey.entries()) {
			const nextSig = itemSignature(parent)
			const prevSig = parentSigByParentKey.get(pk)

			if (nextSig === prevSig) continue

			parentSigByParentKey.set(pk, nextSig)

			const recip = str(parent?.vdprecip)
			if (recip) {
				forcedRecips.add(recip)
				continue
			}

			dirtyParentKeys.push(pk)
		}

		for (const recip of forcedRecips) {
			if (dirtyRecipKeys.has(recip)) continue
			const rawKeys = parentKeysByRecip.get(recip) || []
			if (!rawKeys.length) continue
			dirtyRecipKeys.set(recip, [...rawKeys].sort())
		}

		for (const [recip, parentKeys] of dirtyRecipKeys.entries()) {
			const computed = computeRecipGroupChildren(parentKeys, itemsByParentKey)

			for (const pk of parentKeys) {
				const parent = itemsByParentKey.get(pk)
				if (parent) parentSigByParentKey.set(pk, itemSignature(parent))

				const nextChildren = normalizeChildren(computed.get(pk) || [])
				setChildrenForPk(pk, nextChildren)
			}
		}

		for (const pk of dirtyParentKeys) {
			const parent = itemsByParentKey.get(pk)
			if (!parent) continue

			const base = normalizeChildren(computeBaseChildren(parent))
			setChildrenForPk(pk, base)
		}

		for (const pk of currentParentKeys) {
			if (childrenByParentKey.has(pk)) continue
			const parent = itemsByParentKey.get(pk)
			if (!parent) continue

			parentSigByParentKey.set(pk, itemSignature(parent))
			const base = normalizeChildren(computeBaseChildren(parent))
			setChildrenForPk(pk, base)
		}
	}

	return {
		reset() {
			clearAll()
		},

		getChildrenByParentKey({ soldMap, returnedMap } = {}) {
			const sold = soldMap instanceof Map ? soldMap : new Map()
			const returned = returnedMap instanceof Map ? returnedMap : new Map()

			const hasAnyCache =
				recipSigByRecip.size || parentSigByParentKey.size || childSigByParentKey.size || childrenByParentKey.size

			if (!hasAnyCache) {
				recomputeAll({ soldMap: sold, returnedMap: returned })
				return childrenByParentKey
			}

			updateFromMaps({ soldMap: sold, returnedMap: returned })
			return childrenByParentKey
		},
	}
}
